import {
    Address,
    beginCell,
    Cell,
    Contract,
    contractAddress,
    ContractProvider,
    Sender,
    SendMode,
    toNano,
} from 'ton-core';
import { Dao } from './Dao';
import { readOnchainMetadata } from './onchainContent';
import { ProposalConfig, readProposalConfig } from './Proposal';

export interface NftProposalConfig {
    daoAddress: Address;
    votingId: bigint;
}

export const nftProposalConfigToCell = (config: NftProposalConfig) => {
    return beginCell().storeBit(false).storeAddress(config.daoAddress).storeInt(config.votingId, 64).endCell();
};

export interface NftData {
    name: string;
    image: string;
    description: string;
}
export interface NftProposalData {
    init: boolean;
    daoAddress: Address;
    votingId: bigint;
    expirationDate: Date;
    proposal: ProposalConfig;
    proposalContent: NftData;
    walletCode: Cell;
    votedFor: bigint;
    votedAgainst: bigint;
    executed: boolean;
    ownerAddress: Address;
}

export class NftProposal implements Contract {
    constructor(readonly address: Address, readonly init?: { code: Cell; data: Cell }) {}

    static createFromAddress(address: Address) {
        return new NftProposal(address);
    }

    static createFromConfig(config: NftProposalConfig, code: Cell, workchain = 0) {
        const data = nftProposalConfigToCell(config);
        const init = { code, data };
        return new NftProposal(contractAddress(workchain, init), init);
    }

    async sendTransfer(
        provider: ContractProvider,
        via: Sender,
        newOwnerAddress: Address,
        responseAddress: Address,
        queryId: number = Date.now()
    ) {
        await provider.internal(via, {
            value: toNano('0.1'),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body: Dao.sendNftMessage({
                queryId,
                newOwnerAddress,
                responseAddress,
            }),
        });
    }

    async sendDeploy(
        provider: ContractProvider,
        via: Sender,
        value: bigint,
        params: {
            expirationDate: Date;
            proposal: Cell;
            walletCode: Cell;
            voteKeeperCode: Cell;
            royalty: Cell;
            initiator: Address;
        },
        queryId: number = Date.now()
    ) {
        await provider.internal(via, {
            value,
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body: beginCell()
                .storeUint(0x182d8ddd, 32)
                .storeUint(queryId, 64)
                .storeUint(Math.round(params.expirationDate.getTime() / 1000), 48)
                .storeRef(params.walletCode)
                .storeRef(params.voteKeeperCode)
                .storeRef(params.proposal)
                .storeRef(params.royalty)
                .storeAddress(params.initiator)
                .endCell(),
        });
    }

    static executeMessage(queryId: number = Date.now()) {
        return beginCell().storeUint(0x66173a45, 32).storeUint(queryId, 64).endCell();
    }

    async sendExecute(provider: ContractProvider, via: Sender, value: bigint, queryId: number = Date.now()) {
        await provider.internal(via, {
            value,
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body: NftProposal.executeMessage(queryId),
        });
    }

    async getNftData(provider: ContractProvider) {
        let { stack } = await provider.get('get_nft_data', []);
        let init = stack.readBoolean();
        let votingId = stack.readBigNumber();
        let daoAddress = stack.readAddress();
        let ownerAddress = init ? stack.readAddress() : null;
        let proposal = init ? stack.readCell() : null;

        return {
            init,
            votingId,
            daoAddress,
            ownerAddress,
            proposal,
        };
    }

    async getData(provider: ContractProvider): Promise<NftProposalData> {
        let res = await provider.get('get_voting_data', []);
        let init = res.stack.readBoolean();
        let daoAddress = res.stack.readAddress();
        let votingId = res.stack.readBigNumber();
        let expirationDate = res.stack.readBigNumber();
        let proposal = res.stack.readCell();
        let walletCode = res.stack.readCell();
        let votedFor = res.stack.readBigNumber();
        let votedAgainst = res.stack.readBigNumber();
        let executed = res.stack.readBoolean();
        let ownerAddress = res.stack.readAddress();

        const proposalConfig = readProposalConfig(proposal);
        const proposalContent = readOnchainMetadata<NftData>(proposalConfig.content, ['name', 'image', 'description']);
        return {
            init,
            daoAddress,
            votingId,
            expirationDate: new Date(Number(`${expirationDate.toString()}000`)),
            proposal: proposalConfig,
            proposalContent,
            walletCode,
            votedFor,
            votedAgainst,
            executed,
            ownerAddress,
        };
    }
}
